import { useCallback, useState } from 'react';

import { anonymizeMyAccountApi, exportMyGdprDataApi } from '@/lib/api/usersApi';
import { useAuthStore } from '@/stores/auth-store';
import { AlertTriangle, Download, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface GdprSettingsPanelProps {
  accentColor: 'blue' | 'emerald' | 'orange';
}

const CONFIRM_WORD = 'SUPPRIMER';

export default function GdprSettingsPanel({ accentColor }: GdprSettingsPanelProps) {
  // ── Export state ─────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [exportCooldown, setExportCooldown] = useState(false);

  // ── Anonymize state (triple confirmation) ───────────────
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [confirmInput, setConfirmInput] = useState('');
  const [anonymizing, setAnonymizing] = useState(false);

  const logout = useAuthStore(s => s.logout);

  // ── Export handler ──────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (exporting || exportCooldown) return;
    setExporting(true);
    try {
      const data = await exportMyGdprDataApi();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `boulesconnect-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Vos donnees ont ete telechargees');
      setExportCooldown(true);
      setTimeout(() => setExportCooldown(false), 60_000);
    } catch {
      toast.error("Erreur lors de l'export de vos donnees");
    } finally {
      setExporting(false);
    }
  }, [exporting, exportCooldown]);

  // ── Anonymize handler ──────────────────────────────────
  const handleAnonymize = useCallback(async () => {
    if (anonymizing) return;
    setAnonymizing(true);
    try {
      await anonymizeMyAccountApi();
      toast.success('Votre compte a ete anonymise. Vous allez etre deconnecte.');
      setTimeout(() => {
        logout();
        window.location.href = '/connexion';
      }, 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur lors de l'anonymisation";
      toast.error(message);
      setAnonymizing(false);
    }
  }, [anonymizing, logout]);

  const accentBg = {
    blue: 'bg-blue-50',
    emerald: 'bg-emerald-50',
    orange: 'bg-orange-50',
  }[accentColor];

  const accentIconBg = {
    blue: 'bg-blue-100',
    emerald: 'bg-emerald-100',
    orange: 'bg-orange-100',
  }[accentColor];

  const accentIcon = {
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    orange: 'text-orange-600',
  }[accentColor];

  return (
    <div className='space-y-8 max-w-2xl'>
      {/* ── Section : Export des donnees ────────────────────── */}
      <div className='bg-white rounded-2xl p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-4'>
          <div className={`w-10 h-10 ${accentIconBg} rounded-lg flex items-center justify-center`}>
            <Download className={`w-5 h-5 ${accentIcon}`} />
          </div>
          <div>
            <h3 className='font-bold text-slate-900'>Telecharger mes donnees</h3>
            <p className='text-sm text-slate-500'>Droit d&apos;acces (RGPD Art. 15)</p>
          </div>
        </div>

        <p className='text-sm text-slate-600 leading-relaxed mb-5'>
          Vous pouvez telecharger l&apos;ensemble de vos donnees personnelles stockees sur BoulesConnect au format JSON.
          Ce fichier contient vos informations de profil, vos participations aux concours, et vos fichiers associes.
        </p>

        <button
          onClick={handleExport}
          disabled={exporting || exportCooldown}
          className={`cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition
            ${
              exporting || exportCooldown
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : `${accentBg} ${accentIcon} hover:opacity-80`
            }`}
        >
          <Download className='w-4 h-4' />
          {exporting
            ? 'Export en cours...'
            : exportCooldown
              ? 'Exporte (disponible dans 1 min)'
              : 'Telecharger mes donnees (JSON)'}
        </button>
      </div>

      {/* ── Section : Suppression / Anonymisation du compte ─ */}
      <div className='bg-white rounded-2xl p-6 border border-red-200'>
        <div className='flex items-center gap-3 mb-4'>
          <div className='w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center'>
            <ShieldAlert className='w-5 h-5 text-red-600' />
          </div>
          <div>
            <h3 className='font-bold text-red-900'>Cloture definitive du compte</h3>
            <p className='text-sm text-red-500'>Action irreversible</p>
          </div>
        </div>

        <div className='bg-red-50 border border-red-200 rounded-xl p-4 mb-5'>
          <div className='flex gap-3'>
            <AlertTriangle className='w-5 h-5 text-red-500 shrink-0 mt-0.5' />
            <div className='text-sm text-red-800 space-y-2'>
              <p className='font-semibold'>Attention : cette action est definitive et irreversible.</p>
              <ul className='list-disc pl-4 space-y-1'>
                <li>Vos donnees personnelles seront anonymisees (nom, email, telephone, etc.)</li>
                <li>Votre compte sera desactive et inaccessible</li>
                <li>Vos fichiers (photo de profil, etc.) seront supprimes</li>
                <li>L&apos;historique comptable (paiements, inscriptions) sera conserve de maniere anonyme</li>
                <li>Aucun retour en arriere ne sera possible</li>
              </ul>
              <p>
                <strong>Pensez a telecharger vos donnees avant de continuer.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Step 0 : Initial button */}
        {step === 0 && (
          <button
            onClick={() => setStep(1)}
            className='cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-red-300 text-red-600 font-semibold text-sm transition hover:bg-red-50'
          >
            <Trash2 className='w-4 h-4' />
            Je souhaite supprimer mon compte
          </button>
        )}

        {/* Step 1 : First confirmation */}
        {step === 1 && (
          <div className='border border-red-200 rounded-xl p-4 space-y-4'>
            <p className='text-sm font-semibold text-red-800'>
              Etes-vous certain de vouloir supprimer definitivement votre compte ?
            </p>
            <p className='text-sm text-red-700'>
              Cette action cloturera votre compte de maniere irreversible. Vous ne pourrez plus acceder a vos donnees,
              vos inscriptions, ni utiliser les services BoulesConnect avec ce compte.
            </p>
            <div className='flex items-center gap-3'>
              <button
                onClick={() => setStep(2)}
                className='cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-100 text-red-700 font-semibold text-sm transition hover:bg-red-200'
              >
                Oui, je veux continuer
              </button>
              <button
                onClick={() => setStep(0)}
                className='cursor-pointer px-4 py-2 rounded-xl text-slate-600 font-semibold text-sm transition hover:bg-slate-100'
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Step 2 : Second confirmation with warning */}
        {step === 2 && (
          <div className='border-2 border-red-300 rounded-xl p-4 space-y-4 bg-red-50/50'>
            <div className='flex items-center gap-2 text-red-800'>
              <AlertTriangle className='w-5 h-5' />
              <p className='text-sm font-bold'>Derniere confirmation obligatoire</p>
            </div>
            <p className='text-sm text-red-700'>
              Pour confirmer la suppression definitive de votre compte, tapez{' '}
              <strong className='font-mono bg-red-100 px-1.5 py-0.5 rounded'>{CONFIRM_WORD}</strong> dans le champ
              ci-dessous.
            </p>
            <input
              type='text'
              value={confirmInput}
              onChange={e => setConfirmInput(e.target.value)}
              placeholder={`Tapez ${CONFIRM_WORD} pour confirmer`}
              className='w-full px-4 py-2.5 border border-red-300 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent'
              autoComplete='off'
              spellCheck={false}
            />
            <div className='flex items-center gap-3'>
              <button
                onClick={() => {
                  if (confirmInput === CONFIRM_WORD) setStep(3);
                }}
                disabled={confirmInput !== CONFIRM_WORD}
                className='cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white font-semibold text-sm transition hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed'
              >
                <Trash2 className='w-4 h-4' />
                Confirmer la suppression
              </button>
              <button
                onClick={() => {
                  setStep(0);
                  setConfirmInput('');
                }}
                className='cursor-pointer px-4 py-2 rounded-xl text-slate-600 font-semibold text-sm transition hover:bg-slate-100'
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Step 3 : Final action — point of no return */}
        {step === 3 && (
          <div className='border-2 border-red-500 rounded-xl p-5 space-y-4 bg-red-100/50'>
            <div className='flex items-center gap-2'>
              <ShieldAlert className='w-6 h-6 text-red-600' />
              <p className='text-base font-bold text-red-900'>Point de non-retour</p>
            </div>
            <p className='text-sm text-red-800 font-semibold'>
              En cliquant sur le bouton ci-dessous, votre compte sera immediatement et definitivement anonymise. Vous
              serez deconnecte et ne pourrez plus jamais acceder a ce compte.
            </p>
            <div className='flex items-center gap-3'>
              <button
                onClick={handleAnonymize}
                disabled={anonymizing}
                className='cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-700 text-white font-bold text-sm transition hover:bg-red-800 disabled:opacity-60'
              >
                <Trash2 className='w-4 h-4' />
                {anonymizing ? 'Suppression en cours...' : 'Supprimer definitivement mon compte'}
              </button>
              <button
                onClick={() => {
                  setStep(0);
                  setConfirmInput('');
                }}
                disabled={anonymizing}
                className='cursor-pointer px-4 py-2 rounded-xl text-slate-600 font-semibold text-sm transition hover:bg-slate-100 disabled:opacity-40'
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
